import { TextField } from '@mui/material';
import axios, { CancelTokenSource } from 'axios';
import React, { SetStateAction, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ICampaignContent } from '../../../../../../model/briefing/step2/ICampaignContent';
import { BRAND_BRIEFING } from '../../../../../../utils/endpoints';
import { ErrorToast } from '../../../../../../utils/toasters';

interface Props {
    show: boolean;
    isProductAi: boolean;
    closeModal: () => void;
    setCurrentContent: React.Dispatch<SetStateAction<ICampaignContent | undefined>>;
}

const AiModal = ({ show, isProductAi, closeModal, setCurrentContent }: Props) => {
    const { t } = useTranslation();
    const [aiText, setAiText] = useState('');
    const token = localStorage.getItem('token');
    const [source, setSource] = useState<undefined | CancelTokenSource>(axios.CancelToken.source());
    const [isLoading, setIsLoading] = useState(false);
    const title = isProductAi
        ? t('brand.campaign.briefing.step2.ai_product_title')
        : t('brand.campaign.briefing.step2.ai_briefing_generator');
    const changeAiHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setAiText(value);
    };

    const submitData = async () => {
        setIsLoading(true);

        await axios
            .put(
                `${BRAND_BRIEFING}/generate/text/${aiText}`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token?.slice(1, -1)}`,
                    },
                    cancelToken: source?.token,
                }
            )
            .then(response => {
                const { data } = response;
                setCurrentContent(prev => ({ ...prev, [isProductAi ? 'details' : 'adDescription']: data }));
                setAiText('');
            })
            .catch(() =>
                ErrorToast({
                    response: {
                        data: {
                            message: 'Canceled',
                        },
                    },
                })
            )
            .finally(() => {
                setIsLoading(false);
                closeModal();
            });
    };
    const cancelApiCall = () => {
        source?.cancel('API request cancelled');
        setSource(undefined);
    };
    return (
        <Modal show={show} onHide={closeModal} backdrop="static" keyboard={false} centered>
            <Modal.Body>
                {!isLoading ? (
                    <>
                        <h5>{title}</h5>
                        <p>
                            {isProductAi
                                ? t('brand.campaign.briefing.step2.ai_product_description')
                                : t('brand.campaign.briefing.step2.ai_briefing_generator_description')}
                        </p>

                        <TextField
                            label={title}
                            variant="outlined"
                            size="small"
                            value={aiText ?? ''}
                            className="w-100 mt-3"
                            name={isProductAi ? 'product' : 'briefing'}
                            onChange={changeAiHandler}
                            inputProps={{ maxLength: 1000 }}
                            helperText={`${aiText?.length || 0}/1000`}
                            multiline
                            rows={5}
                        />
                    </>
                ) : (
                    <div className="text-center">
                        <h4 className="text-center">{t('general.generating')}...</h4>
                        <p>{t('general.upTo60s')}</p>
                        <Spinner animation="border" role="status" />
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="outline-primary"
                    onClick={() => {
                        if (isLoading) {
                            cancelApiCall();
                        }
                        closeModal();
                    }}
                >
                    {t('general.button.cancel')}
                </Button>

                {!isLoading && (
                    <Button variant="primary" onClick={submitData}>
                        {t('general.button.generate')}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default AiModal;
